import { inject, injectable, injectFromBase } from 'inversify'
import { RepoSettings } from '@/decorators/repositoryDecorators'
import type { Endpoint } from '@/services/endpoints'
import OrmApiRepository from '@/data/repositories/ormApiRepository'
import UserNumbers from '@/data/models/domain/UserNumbers'
import { TmIncorrectContractError } from '@/core/error/tmIncorrectContractError'
import type { UserNumbersResponse } from '@/services/domain/user/types'
import { SERVICE_TYPES } from '@/core/container/types'
import type SerializerService from '@/services/serializerService'
import type { HttpService } from '@/services/transport/httpService'
import type { AbstractEndpointsInterface } from '@/services/endpointsService'
import type LoggerService from '@/services/loggerService'
import type FilterQueryService from '@/services/filterQueryService'

@RepoSettings<Endpoint>({
  model: UserNumbers,
})
@injectable()
@injectFromBase()
export default class UserNumbersRepository extends OrmApiRepository<UserNumbers> {
  constructor(
    @inject(SERVICE_TYPES.SerializerService) protected readonly serializerService: SerializerService,
    @inject(SERVICE_TYPES.Api) protected readonly api: HttpService,
    @inject(SERVICE_TYPES.EndpointsService) protected readonly endpointsService: AbstractEndpointsInterface,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.FilterQueryService) protected readonly filterQueryService: FilterQueryService,
  ) {
    super(serializerService, api, endpointsService, loggerService, filterQueryService)
  }

  public async fetchUserNumbers(userId?: string) {
    const apiSource = this.getApiSource()
    const path = this.endpointsService.getPath('userNumbers')
    const params = { userId }
    const result = await apiSource.get<UserNumbersResponse>(path, { params })
    if (!result.data) {
      throw new TmIncorrectContractError('Response data expected')
    }
    await this.insertOrUpdateRaw([
      {
        ...result.data,
        id: this.getUserNumbersId(userId),
      },
    ])
    return this.findUserNumbersOrFail(userId)!
  }

  public findUserNumbersOrFail(userId?: string) {
    return this.findEntityByIdOrFail(this.getUserNumbersId(userId))
  }

  public findUserNumbersOrNull(userId?: string) {
    return this.findEntityByIdOrNull(this.getUserNumbersId(userId))
  }

  protected getUserNumbersId(userId?: string) {
    return userId ? `user-${userId}` : 'all'
  }
}
